import APIService from "../components/app/services/APIService";
import { API_BASE_URL } from "../config/apiConfig";

const BASE_URL = "/api/v1/demo";

/**
 * Frontend API service for demo user registration, verification, and admin management.
 * Public endpoints do NOT require auth. Admin endpoints require platform_admin JWT.
 */
const DemoUserService = {
  // ========== Public Endpoints (no JWT) ==========

  /**
   * Register a new demo user.
   * @param {Object} data - { email?, phone?, eventName? }
   * @returns {Promise<Object>} Registration result with registrationToken
   */
  register: async (data) => {
    const response = await fetch(
      `${API_BASE_URL}${BASE_URL}/register`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_KEY,
        },
        credentials: "include",
        body: JSON.stringify(data),
      },
    );
    return response.json();
  },

  /**
   * Verify email with 6-digit code.
   * @param {string} registrationToken
   * @param {string} code - 6-digit code
   */
  verifyEmail: async (registrationToken, code) => {
    const response = await fetch(
      `${API_BASE_URL}${BASE_URL}/verify-email`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_KEY,
        },
        credentials: "include",
        body: JSON.stringify({ registrationToken, code }),
      },
    );
    return response.json();
  },

  /**
   * Verify phone with OTP code.
   * @param {string} registrationToken
   * @param {string} code - OTP code
   */
  verifyPhone: async (registrationToken, code) => {
    const response = await fetch(
      `${API_BASE_URL}${BASE_URL}/verify-phone`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_KEY,
        },
        credentials: "include",
        body: JSON.stringify({ registrationToken, code }),
      },
    );
    return response.json();
  },

  /**
   * Resend email verification code.
   * @param {string} registrationToken
   */
  resendEmailVerification: async (registrationToken) => {
    const response = await fetch(
      `${API_BASE_URL}${BASE_URL}/resend-email`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_KEY,
        },
        credentials: "include",
        body: JSON.stringify({ registrationToken }),
      },
    );
    return response.json();
  },

  /**
   * Resend phone verification code.
   * @param {string} registrationToken
   */
  resendPhoneVerification: async (registrationToken) => {
    const response = await fetch(
      `${API_BASE_URL}${BASE_URL}/resend-phone`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_KEY,
        },
        credentials: "include",
        body: JSON.stringify({ registrationToken }),
      },
    );
    return response.json();
  },

  /**
   * Check registration status.
   * @param {string} registrationToken
   */
  getStatus: async (registrationToken) => {
    const response = await fetch(
      `${API_BASE_URL}${BASE_URL}/status?registrationToken=${encodeURIComponent(registrationToken)}`,
      {
        method: "GET",
        headers: {
          "x-api-key": process.env.REACT_APP_API_KEY,
        },
        credentials: "include",
      },
    );
    return response.json();
  },

  // ========== Admin Endpoints (requires JWT) ==========

  /**
   * Get all pending demo user approvals.
   */
  getPendingApprovals: async () => {
    return APIService.get(`${BASE_URL}/admin/pending`);
  },

  /**
   * Get all demo users.
   */
  getAllDemoUsers: async () => {
    return APIService.get(`${BASE_URL}/admin/all`);
  },

  /**
   * Approve a demo user.
   * @param {string} registrationToken - Demo user registration token
   * @param {number} maxRequests - Max analysis requests (default 10)
   * @param {number} expirationDays - Days until expiry (default 3)
   */
  approve: async (registrationToken, maxRequests = 10, expirationDays = 3) => {
    return APIService.post(`${BASE_URL}/admin/approve`, {
      registrationToken,
      maxRequests,
      expirationDays,
    });
  },

  /**
   * Revoke a demo user.
   * @param {string} registrationToken - Demo user registration token
   */
  revoke: async (registrationToken) => {
    return APIService.post(`${BASE_URL}/admin/revoke`, { registrationToken });
  },

  /**
   * Bulk approve multiple demo users.
   * @param {string[]} registrationTokens - Array of demo user registration tokens
   * @param {number} maxRequests
   * @param {number} expirationDays
   */
  bulkApprove: async (
    registrationTokens,
    maxRequests = 10,
    expirationDays = 3,
  ) => {
    return APIService.post(`${BASE_URL}/admin/bulk-approve`, {
      registrationTokens,
      maxRequests,
      expirationDays,
    });
  },

  /**
   * Reset usage counter for a demo user back to zero.
   * @param {string} registrationToken - Demo user registration token
   */
  resetUsage: async (registrationToken) => {
    return APIService.post(`${BASE_URL}/admin/reset-usage`, {
      registrationToken,
    });
  },

  /**
   * Soft delete a demo user. Their email/phone become available for re-registration.
   * @param {string} registrationToken - Demo user registration token
   */
  softDelete: async (registrationToken) => {
    return APIService.post(`${BASE_URL}/admin/delete`, { registrationToken });
  },
};

export default DemoUserService;
